import { Dialog, DialogTitle, DialogContent, DialogActions, Box } from "@mui/material";
import SoftButton from "components/SoftButton";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import swal from "sweetalert2";
import { api } from "utils/Api";
import { showSuccess } from "utils/messageAlerts";
import { showWarning } from "utils/messageAlerts";

function EditFormPopup({ mode = "create", user = null, onClose }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [description, setDescription] = useState("");
  const [password, setPassword] = useState("");

  // Pre-fill fields when editing
  useEffect(() => {
    if (mode === "edit" && user) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setUsername(user.username || "");
      setEmail(user.email || "");
      setPhoneNumber(user.phone_number || "");
      setDescription(user.description || "");
      setPassword("");
    }
  }, [mode, user]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      name: `${firstName} ${lastName}`.trim(),
      first_name: firstName,
      last_name: lastName,
      username,
      email,
      phone_number: phoneNumber,
      description,
      active: true,
      frontend_metadata: {},
      backend_metadata: {},
    };

    // Only include password if provided
    if (password.trim()) {
      payload.password = password;
    }

    try {
      if (mode === "create") {
        if (!password.trim()) {
          showWarning("Password is required for creating a new user");
          return;
        }
        payload.password = password;
        await api.user.createUser(payload);

        showSuccess("User created successfully");
      } else {
        await api.user.updateUserDetails(user.id, payload);

        showSuccess("User updated successfully");
      }

      onClose();
    } catch (error) {
    }
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>{mode === "create" ? "Add User" : "Edit User"}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "100%", width: "500px" }}>
            {/* First Name */}
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  First Name
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </SoftBox>

            {/* Last Name */}
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Last Name
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </SoftBox>

            {/* Username */}
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Username <Box component="span" color="error.main">*</Box>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </SoftBox>

            {/* Email */}
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Email <Box component="span" color="error.main">*</Box>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </SoftBox>

            {/* Phone Number */}
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Phone Number <Box component="span" color="error.main">*</Box>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="text"
                placeholder="Phone Number (10 digits)"
                value={phoneNumber}
                inputProps={{ maxLength: 10 }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // allow only numbers
                  setPhoneNumber(value);
                }}
                required
              />
            </SoftBox>

            {/* Description */}
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Description
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="text"
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </SoftBox>

            {/* Password */}
            {mode === "create" && (
              <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Password {mode === "edit" && "(leave blank to keep current)"} <Box component="span" color="error.main">*</Box>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </SoftBox>
            )}
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <SoftButton onClick={onClose}>Cancel</SoftButton>
        <SoftButton onClick={handleSubmit} variant="gradient" color="dark" type="submit">
          {mode === "create" ? "Create" : "Update"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

EditFormPopup.propTypes = {
  mode: PropTypes.oneOf(["create", "edit"]),
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default EditFormPopup;